import Analytics, { DEFAULT_ANALYTICS_CONTEXT_VALUE } from "@swiggy-private/analytics";
import { AnalyticsConfig } from "@swiggy-private/analytics/dist/interfaces";

// constants
export const ANALYTICS_HOST = "https://analytics.swiggy.com";
const CHANNEL_KEYS = {
    X_CHANNEL: "x-channel",
};
const CHANNELS = {
    JIOHOTSTAR: "jiohotstar",
};
const HOTSTAR_ANALYTICS_META = {
    APPLICATION_NAME: "jiohotstar",
};

const getChannelFromCookies = (): string => {
    if (typeof document === "undefined") {
        return "";
    }

    const cookies = document.cookie.split(";");
    const xchannelCookie = cookies.find((cookie) => cookie.trim().startsWith(`${CHANNEL_KEYS.X_CHANNEL}=`));
    return xchannelCookie ? xchannelCookie.split("=")[1].trim() : "";
};

const getChannelFromSearchParams = (): string => {
    if (typeof window === "undefined") {
        return "";
    }

    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(CHANNEL_KEYS.X_CHANNEL)?.trim() || "";
};

const getChannel = (headers?: Record<string, string | undefined> | void): string => {
    const headerChannel = headers?.[CHANNEL_KEYS.X_CHANNEL]?.trim() || "";
    const searchParamChannel = getChannelFromSearchParams();
    const cookieChannel = getChannelFromCookies();
    return headerChannel || searchParamChannel || cookieChannel;
};

const isWebview = () => {
    return Boolean(
        typeof window !== "undefined" && (window.AndroidBridge || window.webkit?.messageHandlers?.nativeRequest),
    );
};

export const getAnalyticsConfigParams = (
    headers?: Record<string, string | undefined> | void,
): AnalyticsConfig | undefined => {
    const channel = getChannel(headers).toLowerCase();
    const shouldApplyHotstarMeta = channel === CHANNELS.JIOHOTSTAR;
    const channelAnalyticsMeta = shouldApplyHotstarMeta
        ? {
              applicationName: HOTSTAR_ANALYTICS_META.APPLICATION_NAME,
          }
        : {};

    /**
     * Some pages will open in web browser instead of webview and we want analytics events there.
     * So passing empty values for userInfo with proper analytics host
     */
    if (!isWebview()) {
        return {
            apiHost: ANALYTICS_HOST,
            userInfo: {
                deviceId: "-",
                sid: "-",
                tid: "-",
                userId: "-",
            },
            userLocation: { lat: "", lng: "" },
            platform: "-",
            appVersionCode: "-",
            ...channelAnalyticsMeta,
        };
    }

    if (!headers) {
        return;
    }

    return {
        apiHost: ANALYTICS_HOST,
        userInfo: {
            deviceId: headers.deviceId || "-",
            sid: headers.sid || "-",
            tid: headers.tid || "-",
            userId: headers.userId || "-",
        },
        userLocation: { lat: headers.lat || "", lng: headers.lng || "" },
        platform: headers.analyticsPlatform || "-",
        appVersionCode: headers["version-code"] || "-",
        ...channelAnalyticsMeta,
    };
};

export const getAnalyticsContextValue = (headers?: Record<string, string | undefined> | void) => {
    const analyticsConfig = getAnalyticsConfigParams(headers);
    const analytics = (analyticsConfig && new Analytics(analyticsConfig)) || DEFAULT_ANALYTICS_CONTEXT_VALUE;
    return analytics;
};
